import { motion } from "framer-motion";
import { Download, Mail, ArrowUpRight, MapPin } from "lucide-react";
import { profile } from "../data/portfolio";

export const About = () => {
  return (
    <section
      id="about"
      data-testid="section-about"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 px-5 sm:px-8"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Left — massive name */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-purple-300/80"
            data-testid="about-role-label"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-purple-400 mr-2 align-middle animate-pulse" />
            AI / ML · Generative AI Engineer
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="about-name"
            className="mt-6 font-extrabold tracking-tight leading-[0.95] text-white"
            style={{ fontSize: "clamp(3rem, 9vw, 7rem)" }}
          >
            Riya
            <br />
            Gupta<span className="text-purple-400">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="about-tagline"
            className="mt-8 max-w-xl text-lg text-white/70 leading-relaxed"
          >
            {profile.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <a
              data-testid="about-resume-btn"
              href={profile.resumeUrl}
              target="_blank"
              rel="noreferrer"
              download="Riya-Gupta-Resume.pdf"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-md bg-purple-600 hover:bg-purple-500 text-white font-medium transition-colors"
            >
              <Download size={18} /> Download Resume
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
            <a
              data-testid="about-contact-btn"
              href="#contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-purple-500/40 text-purple-200 hover:bg-purple-500/10 hover:border-purple-400 transition-colors font-medium"
            >
              <Mail size={18} /> Get in touch
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex items-center gap-4 text-sm text-white/50 font-mono"
            data-testid="about-location"
          >
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} className="text-purple-400" />
              {profile.location}
            </span>
            <span className="opacity-40">•</span>
            <span>Available for internships</span>
          </motion.div>
        </div>

        {/* Right — terminal card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5"
          data-testid="about-terminal"
        >
          <div className="rounded-xl border border-white/10 bg-[#0d0620]/70 backdrop-blur-xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(168,85,247,0.35)]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-black/30">
              <div className="flex items-center gap-1.5">
                <span className="term-dot bg-red-400/70" />
                <span className="term-dot bg-yellow-400/70" />
                <span className="term-dot bg-green-400/70" />
              </div>
              <span className="font-mono text-[11px] tracking-wider text-white/40">
                riya@portfolio — zsh
              </span>
              <span className="w-9" />
            </div>

            <div className="p-5 sm:p-6 font-mono text-[13px] leading-relaxed">
              <div className="text-white/40">
                <span className="text-purple-400">$</span> whoami
              </div>
              <div className="text-white mt-1">
                Riya Gupta — AI/ML student · Gen-AI builder
              </div>

              <div className="mt-4 text-white/40">
                <span className="text-purple-400">$</span> cat about.md
              </div>
              <div className="mt-2 space-y-3 text-white/70">
                {profile.about.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-5 text-white/40">
                <span className="text-purple-400">$</span> ls focus/
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {profile.focus.map((f) => (
                  <span
                    key={f}
                    data-testid={`about-focus-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="px-2.5 py-1 rounded-md bg-purple-500/10 border border-purple-500/25 text-purple-200 text-[11px] tracking-wide"
                  >
                    {f}
                  </span>
                ))}
              </div>

              <div className="mt-5 text-white/40">
                <span className="text-purple-400">$</span>
                <span className="cursor-blink" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
